import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock, faXmark } from '@fortawesome/free-solid-svg-icons';
import { P, ease, fontSans, onPhone, shadow } from '../../theme/tokens';

const eyebrowFont = "'Montserrat', -apple-system, sans-serif";

export const FAMILIES = {
  ok: ['#237804', '#F6FFED'],
  warn: ['#9A6212', '#FFF7E6'],
  info: ['#2F6FB0', '#E6F4FF'],
  purple: ['#531DAB', '#F9F0FF'],
  bad: ['#A8071A', '#FFF2F0'],
  neutral: ['#595959', '#F0F0F0'],
  qok: ['#1F6B45', '#E7F4EC'],
  qwarn: ['#8A4B00', '#FDF1DE'],
  qno: ['#3F4652', '#EEF0F3'],
  em: ['#9F1D1D', '#FDECEC'],
  navy: ['#fff', 'var(--brand-primary)']
};

const ChipEl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 18px;
  color: ${({ $f }) => FAMILIES[$f][0]};
  background: ${({ $f }) => FAMILIES[$f][1]};
  svg {
    font-size: 11px;
  }
`;

export function Chip({ family = 'neutral', icon, children, className, style }) {
  return (
    <ChipEl $f={family} className={className} style={style}>
      {icon && <FontAwesomeIcon icon={icon} />}
      {children}
    </ChipEl>
  );
}

export const Mk = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #b7c2d0;
  border-radius: 6px;
  padding: 0 6px;
  font-size: 11px;
  color: #3f4652;
  font-weight: 600;
  white-space: nowrap;
  line-height: 18px;
  background: #fff;
`;

const btnBase = css`
  height: 40px;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  border: 1px solid ${P.border};
  background: #fff;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-size: 14px;
  color: ${P.ink};
  transition: background 150ms;
  &:hover {
    border-color: ${P.navy};
    color: ${P.navy};
  }
  &:focus-visible {
    outline: 2px solid ${P.navyInk};
    outline-offset: 2px;
  }
  ${({ $kind }) =>
    $kind === 'primary' &&
    css`
      background: ${P.grad};
      border: none;
      color: #fff;
      &:hover {
        background: ${P.gradHover};
        color: #fff;
      }
    `}
  ${({ $kind }) =>
    $kind === 'text' &&
    css`
      border: 0;
      background: none;
      color: ${P.navyInk};
      padding: 0 8px;
      &:hover {
        text-decoration: underline;
        color: ${P.navyInk};
      }
    `}
  ${({ $sm }) =>
    $sm &&
    css`
      height: 32px;
      min-height: 32px;
      padding: 0 12px;
      font-size: 13px;
    `}
  &[disabled] {
    background: ${P.line};
    color: ${P.muted};
    border-color: ${P.border};
    cursor: not-allowed;
  }
`;

const BtnEl = styled.button`
  ${btnBase}
`;

export function Btn({ children, icon, kind, sm, ...rest }) {
  return (
    <BtnEl type="button" $kind={kind} $sm={sm} {...rest}>
      {icon && <FontAwesomeIcon icon={icon} />}
      {children}
    </BtnEl>
  );
}

const LockWrap = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
`;
const LockWhy = styled.span`
  font-size: 11px;
  color: ${P.muted};
  max-width: 260px;
`;

export function Lock({ label, reason, sm }) {
  return (
    <LockWrap>
      <BtnEl type="button" disabled $sm={sm} title={reason} aria-label={`${label}. Locked: ${reason}`}>
        <FontAwesomeIcon icon={faLock} />
        {label}
      </BtnEl>
      <LockWhy>{reason}</LockWhy>
    </LockWrap>
  );
}

export const FChip = styled.button.attrs({ type: 'button' })`
  border: 1px solid ${P.border};
  background: #fff;
  border-radius: 999px;
  padding: 0 12px;
  min-height: 32px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${P.ink};
  &[aria-pressed='true'] {
    border-color: var(--brand-primary);
    background: var(--brand-primary);
    color: #fff;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  ${onPhone} {
    min-height: 44px;
  }
`;

const field = css`
  height: 40px;
  border: 1px solid ${P.border};
  border-radius: 8px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 14px;
  background: #fff;
  color: ${P.ink};
`;
export const Inp = styled.input`
  ${field}
`;
const CHEVRON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23595959' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

export const selectChrome = css`
  appearance: none;
  -webkit-appearance: none;
  background: #fff ${CHEVRON} no-repeat right 12px center;
  padding-right: 34px;
  cursor: pointer;
  &:hover {
    border-color: ${P.navy};
  }
  &:focus-visible {
    outline: 2px solid ${P.navyInk};
    outline-offset: 2px;
  }
`;

export const Sel = styled.select`
  ${field}
  ${selectChrome}
`;
export const Area = styled.textarea`
  ${field}
  height: auto;
  padding: 10px 12px;
  resize: vertical;
`;

export const Lbl = styled.label`
  font-size: 12px;
  color: ${P.muted};
`;

export const AzCard = styled.div`
  background: ${({ $sub }) => ($sub ? P.bgSubtle : '#fff')};
  border-radius: 12px;
  border: 1px solid ${P.line};
  box-shadow: ${({ $sub, $flat }) => ($sub || $flat ? 'none' : shadow.sm)};
  padding: 20px;
  position: relative;
  min-width: 0;
`;

export const Eyebrow = styled.div`
  font-family: ${eyebrowFont};
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $light }) => ($light ? '#C9D1DD' : P.muted)};
`;

const SecEl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const SecTitle = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${P.muted};
  font-weight: 700;
`;

export function Sec({ title, children, ...rest }) {
  return (
    <SecEl {...rest}>
      <SecTitle>{title}</SecTitle>
      {children}
    </SecEl>
  );
}

const slidein = keyframes`from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; }`;
const sheetin = keyframes`from { transform: translateY(60px); opacity: 0; } to { transform: none; opacity: 1; }`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 20, 38, 0.35);
  z-index: 1000;
`;
const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 560px;
  max-width: 100%;
  background: #fff;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.15);
  animation: ${slidein} 220ms ${ease};
  font-family: ${fontSans};
  font-size: 14px;
  line-height: 1.45;
  color: ${P.ink};
  ${onPhone} {
    width: 100%;
    top: 44px;
    border-radius: 16px 16px 0 0;
    overflow: hidden;
    animation: ${sheetin} 220ms ${ease};
  }
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 150ms;
  }
`;
const Head = styled.div`
  background: ${P.grad};
  color: #fff;
  padding: 16px 18px;
  flex: none;
`;
const HeadRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;
const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;
const Sub = styled.div`
  font-size: 13px;
  color: #d6dde7;
`;
const Close = styled.button`
  background: rgba(255, 255, 255, 0.12);
  border: 0;
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  cursor: pointer;
  flex: none;
`;
const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 18px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  > * {
    flex-shrink: 0;
  }
`;
const Foot = styled.div`
  flex: none;
  padding: 12px 18px;
  border-top: 1px solid ${P.line};
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: flex-start;
`;

export function AzDrawer({ title, sub, eyebrow, chips, footer, onClose, children }) {
  useEffect(() => {
    const key = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);
  return createPortal(
    <>
      <Scrim onClick={onClose} />
      <Panel role="dialog" aria-modal="true" aria-label={title}>
        <Head>
          <HeadRow>
            <div style={{ flex: 1, minWidth: 0 }}>
              {eyebrow && <Eyebrow $light>{eyebrow}</Eyebrow>}
              <Title>{title}</Title>
              {sub && <Sub>{sub}</Sub>}
            </div>
            <Close type="button" onClick={onClose} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </Close>
          </HeadRow>
          {chips && <Chips>{chips}</Chips>}
        </Head>
        <Body>{children}</Body>
        {footer && <Foot>{footer}</Foot>}
      </Panel>
    </>,
    document.body
  );
}

const DealEl = styled.div`
  display: flex;
  align-items: flex-start;
`;
const DealStep = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  text-align: center;
  position: relative;
  color: ${({ $done }) => ($done ? '#237804' : P.muted)};
  font-weight: ${({ $done }) => ($done ? 600 : 400)};
  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: -50%;
    right: 50%;
    height: 2px;
    background: ${({ $done }) => ($done ? '#52C41A' : P.border)};
  }
  &:first-child:before {
    display: none;
  }
`;
const DealDot = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${({ $done }) => ($done ? '#52C41A' : P.border)};
  background: ${({ $done }) => ($done ? '#52C41A' : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 1;
  color: #fff;
`;

export function Deal({ steps, done }) {
  return (
    <DealEl>
      {steps.map((step, i) => (
        <DealStep key={step} $done={i < done}>
          <DealDot $done={i < done}>{i < done && <FontAwesomeIcon icon={faCheck} />}</DealDot>
          {step}
        </DealStep>
      ))}
    </DealEl>
  );
}

const burst = keyframes`from { transform: translate(0, 0) scale(1); opacity: 1; } to { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; }`;
const BurstWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 20;
  span {
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: ${burst} 600ms ease-out forwards;
  }
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export function Burst() {
  return (
    <BurstWrap aria-hidden="true">
      {Array.from({ length: 10 }, (_, i) => {
        const a = (i / 10) * Math.PI * 2;
        const r = 40 + (i % 3) * 14;
        return <span key={i} style={{ background: i % 2 ? '#52C41A' : 'var(--brand-primary)', '--dx': `${Math.cos(a) * r}px`, '--dy': `${Math.sin(a) * r}px` }} />;
      })}
    </BurstWrap>
  );
}

export const Bubble = styled.div`
  align-self: flex-start;
  background: #0a84ff;
  color: #fff;
  border-radius: 18px;
  padding: 8px 12px;
  max-width: 82%;
  font-size: 14px;
`;

export const LinkBtn = styled.button.attrs({ type: 'button' })`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: ${P.navyInk};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    color: ${P.navy};
  }
`;
